/**
 * @file Powerup definitions and the drop table (GDD §Fase 3 — powerups).
 *
 * A powerup is `{ kind, ...payload }`. `kind` selects the handler inside
 * {@link ../systems/PickupSystem.js}; everything else is data the handler
 * reads. Adding a new powerup that reuses an existing kind (another buff,
 * another weapon) needs no code at all.
 */

import { PALETTE } from './Palette.js';
import { WEAPONS } from './Weapons.js';

/**
 * @typedef {object} PowerupDefinition
 * @property {string} name HUD label.
 * @property {'immortal' | 'shield' | 'buff' | 'ammo' | 'weapon'} kind Handler selector.
 * @property {number} color Body and light colour.
 * @property {number} [value] Shield charge granted.
 * @property {string} [stat] Stat multiplied, for `buff`.
 * @property {number} [multiplier] Multiplier applied, for `buff`.
 * @property {number} [duration] Seconds the effect lasts.
 * @property {string} [weapon] Key into the weapon table, for `weapon`.
 * @property {number} weight Relative drop likelihood.
 */

/** Buff stat names, referenced by systems that read multipliers. */
export const Stat = Object.freeze({
  DAMAGE: 'damage',
  SPEED: 'speed',
  FIRE_RATE: 'fireRate'
});

const STANDARD_WEIGHT = 10;

/** @type {Readonly<Record<string, PowerupDefinition>>} */
export const POWERUPS = Object.freeze({
  health: {
    name: 'Immortal',
    kind: 'immortal',
    color: PALETTE.pickupHealth,
    duration: 10,
    weight: STANDARD_WEIGHT
  },

  shield: {
    name: 'Shield',
    kind: 'shield',
    color: PALETTE.pickupShield,
    value: 60,
    duration: 12,
    weight: STANDARD_WEIGHT
  },

  damage: {
    name: 'x2 Damage!',
    kind: 'buff',
    color: PALETTE.pickupDamage,
    stat: Stat.DAMAGE,
    multiplier: 2,
    duration: 12,
    weight: STANDARD_WEIGHT
  },

  speed: {
    name: 'Super Speed!',
    kind: 'buff',
    color: PALETTE.pickupSpeed,
    stat: Stat.SPEED,
    multiplier: 1.45,
    duration: 14,
    weight: STANDARD_WEIGHT
  },

  fireRate: {
    name: 'Fast shots!',
    kind: 'buff',
    color: PALETTE.pickupFireRate,
    stat: Stat.FIRE_RATE,
    multiplier: 2.1,
    duration: 10,
    weight: STANDARD_WEIGHT
  },

  unlimitedAmmo: {
    name: 'Unlimited Ammo!',
    kind: 'ammo',
    color: PALETTE.pickupFireRate,
    duration: 10,
    weight: STANDARD_WEIGHT
  },

  /**
   * The starting sidearm. Weight 0 keeps it out of the enemy drop table — it
   * only ever reaches the floor when the player swaps it out for something
   * else, which still needs a definition and a prefab to land on.
   */
  pistol: { name: 'Pistol', kind: 'weapon', color: PALETTE.pickupWeapon, weapon: 'pistol', weight: 0 },

  smg: { name: 'SMG', kind: 'weapon', color: PALETTE.pickupWeapon, weapon: 'smg', weight: STANDARD_WEIGHT },
  revolver: {
    name: 'Revolver',
    kind: 'weapon',
    color: PALETTE.pickupWeapon,
    weapon: 'revolver',
    weight: STANDARD_WEIGHT
  },
  gatling: {
    name: 'Gatling',
    kind: 'weapon',
    color: PALETTE.pickupWeapon,
    weapon: 'gatling',
    weight: STANDARD_WEIGHT
  },
  shotgun: {
    name: 'Shotgun',
    kind: 'weapon',
    color: PALETTE.pickupWeapon,
    weapon: 'shotgun',
    weight: STANDARD_WEIGHT
  },
  railgun: {
    name: 'Sniper',
    kind: 'weapon',
    color: PALETTE.pickupWeapon,
    weapon: 'railgun',
    weight: STANDARD_WEIGHT
  },

  rocket_launcher: {
    name: 'Rocket Launcher',
    kind: 'weapon',
    color: PALETTE.pickupWeapon,
    weapon: 'rocket_launcher',
    weight: STANDARD_WEIGHT
  }
});

/**
 * Pickup definitions for the weapons the random drop table never rolls: the
 * player's starting sidearm and the enemy-only guns. An enemy drops whatever it
 * was actually carrying, so every weapon needs a definition to land on the floor
 * with. Keys are disjoint from {@link POWERUPS} by construction.
 *
 * @type {Readonly<Record<string, PowerupDefinition>>}
 */
export const WEAPON_PICKUPS = Object.freeze(
  Object.fromEntries(
    Object.entries(WEAPONS)
      .filter(([key]) => key !== 'flamethrower' && !Object.hasOwn(POWERUPS, key))
      .map(([key, archetype]) => [
        key,
        Object.freeze({
          name: archetype.name,
          kind: 'weapon',
          color: PALETTE.pickupWeapon,
          weapon: key,
          // Never rolled: `rollPowerup` only ever walks the POWERUPS table.
          weight: 0
        })
      ])
  )
);

/**
 * Resolves a pickup key against both tables.
 * @param {string} key Powerup key or weapon archetype key.
 * @returns {PowerupDefinition | null}
 */
export function pickupDefinition(key) {
  return POWERUPS[key] ?? WEAPON_PICKUPS[key] ?? null;
}

/** Tuning for how powerups enter the world. */
export const DROPS = Object.freeze({
  /** Base chance an enemy drops anything, before its own `dropBonus`. */
  chance: 0.14,
  /** Seconds a dropped powerup stays on the ground before despawning. */
  lifetime: 22,
  /**
   * Seconds a dropped weapon stays on the ground. Longer than a powerup's:
   * swapping is a decision, and the player is meant to be able to come back.
   */
  weaponLifetime: 30,
  /** Radius at which the player vacuums it up. */
  pickupRadius: 1.5,
  /** Visual bob and spin. */
  bobHeight: 0.22,
  bobSpeed: 2.6,
  spinSpeed: 1.8,
  hoverHeight: 0.9,
  /** How far from the player a swapped-out weapon lands. */
  dropOffset: 0.55,
  /** Seconds between two weapon pickups, so one step cannot chain a swap. */
  weaponSwapCooldown: 0.15
});

/**
 * Reverse index from weapon archetype key to the powerup that grants it, so a
 * weapon the player is carrying can be turned back into a floor pickup.
 * @type {Readonly<Record<string, string>>}
 */
const WEAPON_POWERUP_KEYS = Object.freeze(
  Object.fromEntries(
    Object.entries(POWERUPS)
      .filter(([, definition]) => definition.kind === 'weapon')
      .map(([key, definition]) => [definition.weapon, key])
  )
);

/**
 * @param {string} weaponKey Key into {@link ../config/Weapons.js WEAPONS}.
 * @returns {string | null} Key into {@link POWERUPS}, or null when the weapon
 *   has no floor representation (enemy-only sidearms).
 */
export function weaponPickupKey(weaponKey) {
  return WEAPON_POWERUP_KEYS[weaponKey] ?? null;
}

/**
 * Weighted pick over a list of `[key, definition]` entries.
 * @param {[string, PowerupDefinition][]} entries Candidates, all with `weight > 0`.
 * @returns {string} The chosen key.
 */
function weightedPick(entries) {
  let total = 0;
  for (const [, definition] of entries) total += definition.weight;

  let roll = Math.random() * total;
  for (const [key, definition] of entries) {
    roll -= definition.weight;
    if (roll <= 0) return key;
  }
  return entries[0][0];
}

/**
 * Picks a powerup key using the weighted drop table. Zero-weight entries exist
 * only as swap drops and never come out of the roll.
 * @returns {string} Key into {@link POWERUPS}.
 */
export function rollPowerup() {
  const entries = Object.entries(POWERUPS).filter(([, definition]) => definition.weight > 0);
  return weightedPick(entries);
}

/**
 * Picks a weapon-only powerup key, ignoring heals/shields/buffs. Used by the
 * shrine altar, which always grants a gun rather than a random effect.
 * @returns {string} Key into {@link POWERUPS}.
 */
export function rollWeaponPowerup() {
  const entries = Object.entries(POWERUPS).filter(
    ([, definition]) => definition.kind === 'weapon' && definition.weight > 0
  );
  return weightedPick(entries);
}
